import { useTranslation } from 'react-i18next';
import { Card } from '#pie/components/card/Card';
import { DescriptionList } from '#pie/components/description-list/DescriptionList';
import { DescriptionListItem } from '#pie/components/description-list/description-list-item/DescriptionListItem';
import { Icon } from '#pie/components/icon/Icon';
import { InfoTooltip } from '#pie/components/info-tooltip/InfoTooltip';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { getFormattedDate } from '#pie/i18n/dateFormats';
import type { MarketPartyRegistrationDto } from '#pie/internal/api/pie-bff';
import { getProductKindIcon } from '#pie/utils/textToIcon';

interface MarketPartyPreferredPartiesProps {
  marketParty: MarketPartyRegistrationDto;
}

export const MarketPartyPreferredParties = ({
  marketParty,
}: MarketPartyPreferredPartiesProps) => {
  const { t } = useTranslation();
  const { t: tmp } = useTranslation('marketParty');

  return (
    <Card
      size="lg"
      heading={
        <Stack direction="row" gap="sm">
          <Text as="h2" variant="h6" className="text-content-heading">
            {tmp('preferred_party.title')}
          </Text>
          <InfoTooltip
            className="text-content-heading"
            aria-label={tmp('preferred_party.info.aria-label')}
            size={20}
          >
            {tmp('preferred_party.title.tooltip')}
          </InfoTooltip>
        </Stack>
      }
      headingUnderline={false}
    >
      {marketParty.preferences?.map((preference, index) => (
        <Stack gap="sm" className="mb-4" key={index}>
          <Card
            key={index}
            size="md"
            heading={
              <Stack direction="row" gap="xs" align="center">
                {getProductKindIcon(preference.product) && (
                  <Icon
                    icon={getProductKindIcon(preference.product)!}
                    size={24}
                    className="text-content-heading"
                  />
                )}
                <Text
                  as="h2"
                  variant="base"
                  className="text-content-heading font-bold"
                >
                  {t(`common.product${preference.product}`, {
                    defaultValue: '',
                  })}
                </Text>
              </Stack>
            }
          >
            <DescriptionList variant="grid">
              <DescriptionListItem
                direction="vertical"
                title={tmp('registration_detail.statutory_name')}
              >
                {preference.statutoryName || '-'}
              </DescriptionListItem>
              <DescriptionListItem
                direction="vertical"
                title={tmp('preferred_party.eancode')}
              >
                {preference.marketParticipantEanCode || '-'}
              </DescriptionListItem>
              <DescriptionListItem
                direction="vertical"
                title={tmp('startdate')}
              >
                {(preference.startDate &&
                  getFormattedDate(new Date(preference.startDate), false)) ||
                  '-'}
              </DescriptionListItem>
              <DescriptionListItem direction="vertical" title={tmp('enddate')}>
                {(preference.endDate &&
                  getFormattedDate(new Date(preference.endDate), false)) ||
                  tmp('license.title_indefinite')}
              </DescriptionListItem>
            </DescriptionList>
          </Card>
        </Stack>
      ))}
    </Card>
  );
};
